import type { AutomergeUrl } from "@automerge/automerge-repo";
import { RepoContext } from "@automerge/react";
import { DEFAULT_DRAFTER_MODEL_ID } from "apprentices/worker/models";
import { Composer } from "forest/chat/Composer";
import { usePlaygroundFileContext } from "forest/chat/file-mentions";
import type { SentMessage } from "forest/chat/types";
import { useTreeRevision } from "forest/file-tree/use-tree";
import { GitBranch, Loader2 } from "lucide-react";
import { createPrivateBlobStore } from "playground/hal/blobStore/private";
import type { HostPlayground } from "playground/hal/index";
import { createPlayground } from "playground/hal/playground";
import { useContext, useEffect, useState } from "react";

import { DrafterModelPicker } from "@/components/DrafterModelPicker";
import { Kbd } from "@/components/ui/kbd";
export function WorkshopPrompt({
  projectId,
  indexUrl,
  creating,
  onStart,
}: {
  projectId: string;
  indexUrl: AutomergeUrl;
  creating: boolean;
  onStart: (message: SentMessage, modelId: string) => void;
}) {
  const repo = useContext(RepoContext);
  const [playground, setPlayground] = useState<HostPlayground | null>(null);
  const [modelId, setModelId] = useState(DEFAULT_DRAFTER_MODEL_ID);
  useEffect(() => {
    if (!repo) return;
    let live = true;
    void createPlayground({
      repo,
      root: indexUrl,
      blobs: createPrivateBlobStore({
        presignUrl: `/api/projects/${projectId}/uploads`,
      }),
    }).then((next) => {
      if (live) setPlayground(next);
    });
    return () => {
      live = false;
    };
  }, [repo, indexUrl, projectId]);
  const fileContext = usePlaygroundFileContext(
    playground,
    useTreeRevision(playground),
  );

  return (
    <div className="workshop-prompt">
      <Composer
        className="workshop-composer"
        onSend={(message) => onStart(message, modelId)}
        status="ready"
        placeholder="Describe a task, ask a question, or investigate a change…"
        textareaDisabled={creating}
        submitDisabled={creating}
        disableEmptySubmit
        allowAttachments
        fileContext={fileContext}
        mentionPlacement="below"
        openFilePath={null}
        footerLeading={
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <GitBranch className="size-3.5" /> From main
          </span>
        }
        footerExtras={({ disabled }) => (
          <DrafterModelPicker
            value={modelId}
            onChange={setModelId}
            disabled={disabled}
          />
        )}
        submitAriaLabel={creating ? "Starting agent" : "Start agent"}
        submitLabel={
          creating ? (
            <>
              <Loader2 data-icon="inline-start" className="animate-spin" />
              Starting agent…
            </>
          ) : (
            <>
              Start agent
              <Kbd
                aria-hidden="true"
                className="bg-primary-foreground/20 text-primary-foreground"
              >
                ↵
              </Kbd>
            </>
          )
        }
      />
    </div>
  );
}
